import { showNotification } from "@mantine/notifications";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { customAxios } from "lib/axios";

export interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
}

export interface OverviewCounts {
  totalProjects: number;
  businessProjects: number;
  marketingProjects: number;
  softwareProjects: number;
  totalIssues: number;
  completedIssues: number;
  todoIssues: number;
  inProgressIssues: number;
  inReviewIssues: number;
  completionRate: number;
}

export interface Task {
  _id: string;
  project: string;
  type: string;
  priority: string;
  status: string;
  title: string;
  reporter: string;
  assignee: string;
  createdAt: string;
  updatedAt: string;
  dueDate: string;
  completedAt?: string;
  __v: number;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  category: string;
  members: { id: string; email: string }[];
  tasks: {
    todo: Task[];
    inProgress: Task[];
    inReview: Task[];
    completed: Task[];
  };
  taskStats: {
    total: number;
    todo: number;
    inProgress: number;
    inReview: number;
    completed: number;
    completionRate: number;
  };
}

export interface ClientOverviewResponse {
  success: boolean;
  data: {
    client: Client;
    overview: OverviewCounts;
    projects: Project[];
  };
}

export const fetchClientOverview = async (
  clientId: string
): Promise<ClientOverviewResponse["data"]> => {
  const { data } = await customAxios.get<ClientOverviewResponse>(
    `/client/overview/${clientId}`
  );
  return data.data;
};

export const useClientOverview = (clientId: string) =>
  useQuery<ClientOverviewResponse["data"], AxiosError>(
    ["clientOverview", clientId],
    () => fetchClientOverview(clientId),
    {
      onError: (error) =>
        showNotification({
          title: "Error Loading Client Overview",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to load client overview",
          color: "red",
        }),
      staleTime: 1000 * 60 * 5,
      retry: 1,
    }
  );
